/**
 * DecisionNode
 *
 * Represents a binary Yes/No decision point in the workflow. After each major
 * step (identity proof, selfie checks, additional background checks), a decision
 * node determines which branch to follow based on the outcome.
 *
 * Decision nodes used in this flowchart:
 *   1. "IDENTITY PROOF VALID"              — after the Identity Proof group
 *   2. "FACE MATCH PASSED"                 — after the Selfie step (inside group)
 *   3. "LIVENESS CONFIDENCE SCORE HIGH"    — after Face Match (inside group)
 *   4. "GLOBAL WATCHLIST RECORD FOUND"     — after Global Watchlist check
 *   5. "AUTHORITATIVE DB CHECK PASSED"     — after AuthDB check
 *   6. "SEVEN YEAR CRIMINAL RECORD FOUND"  — after Seven Year check
 *
 * Each decision node connects to two terminal nodes (Clear / Needs Review / Error)
 * via YesNoEdge. Note: for watchlist and criminal record decisions, Yes is a
 * negative outcome (record found), so the edge colors are inverted in YesNoEdge.
 *
 * Handles:
 *   - target-top:    receives the edge from the step above (check node or group)
 *   - target-left:   alternative entry point (used in some layout configurations)
 *   - source-bottom: Yes or No branch going downward to a terminal or next check
 *   - source-right:  Yes or No branch going right to a terminal node
 *   - source-left:   Yes or No branch going left to a terminal node
 *
 * Data props:
 *   - label: the decision question in uppercase (e.g. "IDENTITY PROOF VALID")
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

import { ReactComponent as CheckIcon } from 'asset/IconsPack/at_check-circle-filled.svg';

function DecisionNode({ data }) {
    return (
        <Box className={styles.decisionNode}>
            <CheckIcon className={styles.decisionNodeIcon} />
            <Typography component="span" className={styles.decisionLabel}>
                {data.label}
            </Typography>

            {/* Receives the edge from the step directly above (check node or group bottom) */}
            <Handle type="target" position={Position.Top} id="target-top" style={{ opacity: 0 }} />

            {/* Alternative entry — used when an edge arrives from the left side */}
            <Handle
                type="target"
                position={Position.Left}
                id="target-left"
                style={{ opacity: 0 }}
            />

            {/* Yes/No branches — which handle is used depends on the target terminal's position */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Right}
                id="source-right"
                style={{ opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Left}
                id="source-left"
                style={{ opacity: 0 }}
            />
        </Box>
    );
}

DecisionNode.propTypes = {
    data: PropTypes.shape({
        label: PropTypes.string.isRequired,
    }).isRequired,
};

export default DecisionNode;
